import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { InPageDialogHost } from "@/components/InPageDialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { lazy, Suspense, useState, useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { isAppBuild, tokenScope } from "@/lib/net";
import { publicTabFor } from "@/lib/appPublicTabs";
import { RouteScrollReset } from "@/lib/routeScroll";
import { isCrewWebPath } from "@/crew/schedule";
import AppComingSoon from "@/components/AppComingSoon";
import { appRoleHome } from "@/lib/appRoleHome";
import { rememberAppReturnTo, consumeAppReturnTo, shouldRememberSignedOutPath } from "@/lib/appReturnTo";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useFirstPartyAnalytics } from "@/hooks/useFirstPartyAnalytics";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Header from "@/components/layout/header";
import Sidebar from "@/components/layout/sidebar";
import MobileTabBar from "@/components/layout/mobile-tab-bar";
import { useStaleBuildReload } from "@/lib/staleBuild";
import { honourViewClassic, isClientSideRole, isUiV2Enabled } from "@/v2/lib/flag";
import { isManagerV2Enabled, isNarrowViewport, subscribeNarrowViewport } from "@/v2/manager/lib/app/flag";
import { LiveCallPopup } from "@/components/admin/LiveCallPopup";
import { isLaMirageInHost } from "@/lib/laMirageInHost";
import { isDriveHost } from "@/lib/driveHost";
// Client pages - Lazy loaded for smaller initial bundle
const Dashboard = lazy(() => import("@/pages/dashboard"));
// Client portal v2 preview shell (flag-gated, see client/src/v2/lib/flag.ts).
const V2ClientApp = lazy(() => import("@/v2/V2ClientApp"));
/* force-v2 Part 4: the last route of the classic client table V2ClientApp falls through to. */
const V2NotFound = lazy(() => import("@/v2/pages/NotFound"));
// Manager v2 shell — ON for phone/tablet widths (< 1024px) unless the server switch is off; `?mgr=v2` /
// `?mgr=v1` override per browser; admin / event_manager / operations_manager only. See client/src/v2/manager/lib/app/flag.ts.
const ManagerV2App = lazy(() => import("@/v2/manager/ManagerApp"));
// Store app, Track C: crew/staff sign-in screens (Task C4).
const CrewOtpSignIn = lazy(() => import("@/components/auth/CrewOtpSignIn"));
const StaffSignIn = lazy(() => import("@/components/auth/StaffSignIn"));
const AppPublicShell = lazy(() => import("@/components/app/AppPublicShell"));
const CrewApp = lazy(() => import("@/crew/CrewApp"));
// Task D6: push permission sheet on each signed-in app home (renders nothing on the web).
const AppPushPrompt = lazy(() => import("@/components/app/AppPushPrompt"));
// Store app only (Task B6): what a manager path with no v2 screen renders inside the app.
const AppClassicPage = lazy(() => import("@/v2/manager/app/AppClassicPage"));
const GuestManagement = lazy(() => import("@/pages/guest-management"));
const Invitations = lazy(() => import("@/pages/invitations"));
const BudgetCalculator = lazy(() => import("@/pages/budget-calculator"));
const WeddingChecklist = lazy(() => import("@/pages/wedding-checklist"));
const WeddingTimeline = lazy(() => import("@/pages/wedding-timeline"));
const WeddingWebsiteWizard = lazy(() => import("@/pages/wedding-website-wizard"));
const StoryWeaverPage = lazy(() => import("@/pages/story-weaver/StoryWeaverPage"));
const WeddingWebsiteManage = lazy(() => import("@/pages/wedding-website-manage"));
const WeddingWebsiteEditor = lazy(() => import("@/pages/wedding-website-editor"));
const WeddingNotes = lazy(() => import("@/pages/wedding-notes"));
const MenuPlanning = lazy(() => import("@/pages/menu-planning"));
const EventForm = lazy(() => import("@/pages/event-form"));
const MCScheduling = lazy(() => import("@/pages/mc-scheduling"));
const Bookings = lazy(() => import("@/pages/bookings"));
const WeddingVendors = lazy(() => import("@/pages/wedding-vendors"));
const EventsCalendar = lazy(() => import("@/pages/events-calendar"));
const MFAManagement = lazy(() => import("@/pages/MFAManagement"));
const EventPayments = lazy(() => import("@/pages/event-payments"));
const Invoices = lazy(() => import("@/pages/invoices"));
const ValetBooking = lazy(() => import("@/pages/valet"));
const ValetRequest = lazy(() => import("@/pages/valet-request"));
const SupportTickets = lazy(() => import("@/pages/support-tickets"));
const SupportWorkspace = lazy(() => import("@/pages/admin/support-workspace"));
const Files = lazy(() => import("@/pages/files"));
// StageDecorationPage deleted 2026-07-09 (Phase D): it was imported but never
// routed — /stage-decoration/:id renders EventForm. Its orphaned 20-step wizard
// chain (WeddingStageManager etc.) was a second, divergent MC-selection
// implementation waiting to be resurrected by mistake.
const ClientOnboarding = lazy(() => import("@/pages/client-onboarding"));
const GuestRSVP = lazy(() => import("@/pages/guest-rsvp"));
const LiveWatch = lazy(() => import("@/pages/watch/LiveWatch"));
const SignContract = lazy(() => import("@/pages/sign/SignContract"));
const ViewInvoice = lazy(() => import("@/pages/inv/ViewInvoice"));
const Packages = lazy(() => import("@/pages/packages"));
const PackageDetails = lazy(() => import("@/pages/package-details"));
const AIAssistantPage = lazy(() => import("@/pages/ai-assistant"));
const MenuBuilderPage = lazy(() => import("@/pages/menu-builder"));
const WeddingWebsitePublic = lazy(() => import("@/pages/wedding-website-public"));

// la-mirage.in — the public booking funnel. Lazy, so not a byte of it lands in
// the lamirage.co portal's initial bundle: on that host the routes below are
// never registered and the chunk is never requested.
const BookingFunnel = lazy(() => import("@/pages/public/booking/BookingFunnel"));
const PricingPage = lazy(() => import("@/pages/public/booking/PricingPage"));
const BookingConfirming = lazy(() => import("@/pages/public/booking/BookingConfirming"));

// A custom domain can be a couple's LIVE-stream domain OR their wedding-WEBSITE
// domain — same hostname shape. Ask the backend which; render LiveWatch or the
// wedding website accordingly.
function CustomDomainRouter() {
  const [target, setTarget] = useState<"loading" | "live" | "website">("loading");
  useEffect(() => {
    const host = window.location.hostname.toLowerCase().replace(/^www\./, "");
    fetch(`/api/live/by-custom-domain/${encodeURIComponent(host)}`)
      .then((r) => setTarget(r.ok ? "live" : "website"))
      .catch(() => setTarget("website"));
  }, []);
  if (target === "loading") return <PageLoader />;
  return (
    <Suspense fallback={<PageLoader />}>
      {target === "live" ? <LiveWatch /> : <WeddingWebsitePublic />}
    </Suspense>
  );
}
const GuestPhotoUpload = lazy(() => import("@/pages/guest-photo-upload"));
// M-2 (2026-09-29): the old guest pages (live-photo-register / live-photo-gallery)
// are no longer routed — their URLs forward to the ONE guest page, /gallery/<slug>.
const LegacyGalleryLink = lazy(() => import("@/pages/live-photos-legacy-link").then((m) => ({ default: m.LegacyGalleryLink })));
const LegacyRegisterLink = lazy(() => import("@/pages/live-photos-legacy-link").then((m) => ({ default: m.LegacyRegisterLink })));
const ClientLivePhotos = lazy(() => import("@/pages/client-live-photos"));
const PublicGallery = lazy(() => import("@/pages/public-gallery"));
const AccountDeletion = lazy(() => import("@/pages/public/AccountDeletion"));
const Booth = lazy(() => import("@/pages/booth"));
const LiveSlideshow = lazy(() => import("@/pages/live-slideshow"));
const LiveSlideshowTV = lazy(() => import("@/pages/live-slideshow-tv"));
const PicsLanding = lazy(() => import("@/pages/pics-landing"));
const DrivePage = lazy(() => import('@/pages/drive'));
const HelpCenter = lazy(() => import("@/pages/help-center"));
const ConciergeMode = lazy(() => import("@/pages/concierge-mode"));
const PhotographerApp = lazy(() => import("@/pages/photographer-app"));
const PrintStation = lazy(() => import("@/pages/print-station"));
const FamilyHub = lazy(() => import("@/pages/family-hub"));
const ClientTransport = lazy(() => import("@/pages/client-transport"));
const EventScreens = lazy(() => import("@/pages/event-screens"));
const AcceptInvite = lazy(() => import("@/pages/accept-invite"));
const SalaryAttendance = lazy(() => import("@/pages/SalaryAttendance"));
const SalaryAdminApproval = lazy(() => import("@/pages/admin/SalaryAdminApproval"));
const IntelligenceDashboard = lazy(() => import("@/pages/intelligence-dashboard"));

// Vendor pages - Lazy loaded
const VendorDirectory = lazy(() => import("@/pages/vendor-directory"));
const VendorMarketplace = lazy(() => import("@/pages/vendor-marketplace"));
const VendorSignup = lazy(() => import("@/pages/vendor-signup"));
const VendorLogin = lazy(() => import("@/pages/vendor-login"));
const VendorDashboard = lazy(() => import("@/pages/vendor-dashboard"));
const VendorProductsStandalone = lazy(() => import("@/pages/vendor-products-standalone"));
const VendorMessages = lazy(() => import("@/pages/vendor-messages"));
const VendorProfile = lazy(() => import("@/pages/vendor-profile"));
const VendorBookings = lazy(() => import("@/pages/vendor-bookings"));
const VendorReviews = lazy(() => import("@/pages/vendor-reviews"));

// Admin pages - Lazy loaded
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminWeddings = lazy(() => import("@/pages/admin-weddings"));
const AdminVendorsRestored = lazy(() => import("@/pages/admin-vendors-restored"));
const AdminAnalytics = lazy(() => import("@/pages/admin/analytics/AnalyticsLayout"));
const AdminFiles = lazy(() => import("@/pages/admin-files"));
const AdminStorage = lazy(() => import("@/pages/admin-storage"));
const AdminClients = lazy(() => import("@/pages/admin-clients"));
const AdminClientDetails = lazy(() => import("@/pages/admin-client-details"));
const AdminReviewManagement = lazy(() => import("@/pages/admin-review-management"));
const AdminLoyalty = lazy(() => import("@/pages/admin-loyalty"));
const AdminRescheduleRequests = lazy(() => import("@/pages/admin-reschedule-requests"));
const AdminSignageHub = lazy(() => import("@/pages/admin-signage-hub"));
const AdminSignageGuestPhotos = lazy(() => import("@/pages/admin-signage-guest-photos"));
const AdminSignageLiveWalls = lazy(() => import("@/pages/admin-signage-live-walls"));
const AdminSignageLive = lazy(() => import("@/pages/admin-signage-live"));
const AdminEmployeeCalendar = lazy(() => import("@/pages/admin-employee-calendar"));
const AdminMCManagement = lazy(() => import("@/pages/admin-mc-management-unified"));
const AdminMCSchedules = lazy(() => import("@/pages/admin/mc-schedules"));
const AdminMCCalendar = lazy(() => import("@/pages/admin/mc-calendar"));
const AdminDanceCalendar = lazy(() => import("@/pages/admin/dance-calendar"));
const AdminSettings = lazy(() => import("@/pages/admin-settings"));
const AdminMobileNavigationMinimal = lazy(() => import("@/pages/admin-mobile-navigation-minimal"));
const MobileAppManagement = lazy(() => import("@/pages/admin/MobileAppManagement"));
const ValetAdmin = lazy(() => import("@/pages/valet-admin"));
const ValetTVDisplay = lazy(() => import("@/pages/valet-tv"));
const ValetDeskAlert = lazy(() => import("@/pages/valet-alert"));
const ZohoActivities = lazy(() => import("@/pages/zoho-activities"));
const ZohoSettings = lazy(() => import("@/pages/zoho-settings"));
const ZohoDeskSettings = lazy(() => import("@/pages/zoho-desk-settings"));
const WhatsAppTemplatesAdmin = lazy(() => import("@/pages/admin/whatsapp-templates"));
const AgentsPage = lazy(() => import("@/pages/admin/Agents"));
const FinancialReports = lazy(() => import("@/pages/financial-reports"));
const AdminEmailManagement = lazy(() => import("@/pages/admin-email-management"));
const AdminPackages = lazy(() => import("@/pages/admin-packages"));
const AdminPerformance = lazy(() => import("@/pages/AdminPerformance"));
const AdminAbandonedBookings = lazy(() => import("@/pages/admin/abandoned-bookings"));
const AdminBookings = lazy(() => import("@/pages/admin/bookings"));
const AdminJourneyStudio = lazy(() => import("@/pages/admin/journey-studio"));
const AdminAIKnowledge = lazy(() => import("@/pages/admin-ai-knowledge"));
const AdminDomainRequests = lazy(() => import("@/pages/admin-domain-requests"));

// More admin pages - Lazy loaded
const AdminAIAssistantPage = lazy(() => import("@/pages/admin-ai-assistant"));
const StageDesignManagement = lazy(() => import("@/components/admin/StageDesignManagement"));
const AdminFlowerCombinations = lazy(() => import("@/pages/admin-flower-combinations"));
const AdminOTPTesting = lazy(() => import("@/pages/AdminOTPTesting"));
const AdminWelcomeConfig = lazy(() => import("@/pages/admin-welcome-config"));
const StoryWeaverAdmin = lazy(() => import("@/pages/admin/StoryWeaverAdmin"));
const AdminTimelineDetails = lazy(() => import("@/pages/admin-timeline-details"));
const AdminVenueConfig = lazy(() => import("@/pages/admin-venue-config"));
const AdminPreEventOperations = lazy(() => import("@/pages/admin-pre-event-operations"));
const AdminEventTimeOperations = lazy(() => import("@/pages/admin-event-time-operations"));
const AdminPostEventOperations = lazy(() => import("@/pages/admin-post-event-operations"));
const AdminStageSelections = lazy(() => import("@/pages/admin-stage-selections"));
const AdminMcEdit = lazy(() => import("@/pages/admin-mc-edit"));
const AdminValetManagement = lazy(() => import("@/pages/admin-valet-management"));
const AdminAddons = lazy(() => import("@/pages/admin-addons"));
const FinancialDashboard = lazy(() => import("@/pages/admin/FinancialDashboard"));
const FinanceHub = lazy(() => import("@/pages/admin/FinanceHub"));
const EventsHub = lazy(() => import("@/pages/admin/EventsHub"));
const VisionStudioAdmin = lazy(() => import("@/pages/admin/VisionStudioAdmin"));
const AdminCatering = lazy(() => import("@/pages/admin-catering"));
const AdminDanceApprovals = lazy(() => import("@/pages/admin-dance-approvals"));
const AIAdminDashboard = lazy(() => import("./pages/AIAdminDashboard"));
const InventoryDashboard = lazy(() => import("@/pages/InventoryDashboard"));
const AdminBackups = lazy(() => import("@/pages/admin-backups"));
const AdminN8n = lazy(() => import("@/pages/admin-n8n"));
// CallerDesk page retired 2026-04-22 — /admin/callerdesk now redirects to
// /admin/voice-call-history which has the same 4-tab UX backed by Asterisk
// voice_ai_calls data. The CallerDeskDashboard.tsx file is kept in the repo
// for archival reference only (no longer routed).
const NotificationAdmin = lazy(() => import("@/pages/admin/NotificationAdmin"));
const VoiceAgentAdmin = lazy(() => import("@/pages/admin/voice-agent"));
const VoiceCallHistory = lazy(() => import("@/pages/admin/voice-call-history"));
const WebCallTest = lazy(() => import("@/pages/admin/web-call-test"));
const WebCallFunnel = lazy(() => import("@/pages/admin/web-call-funnel"));
const SecurityPage = lazy(() => import("@/pages/admin/security"));
const PayoutProvidersPage = lazy(() => import("@/pages/admin/payout-providers"));
const LocalPurchaseManagement = lazy(() => import("@/pages/LocalPurchaseManagement"));
const CateringAdvances = lazy(() => import("@/pages/CateringAdvances"));
const ArtistsManagement = lazy(() => import("@/pages/admin/ArtistsManagement"));
const PhotographerDevices = lazy(() => import("@/pages/admin/photographer-devices"));
const LivePhotoDashboard = lazy(() => import("@/pages/admin/LivePhotoDashboard"));
const AdminLivePhotos = lazy(() => import("@/pages/admin/AdminLivePhotos"));
const LiveStreamingAdmin = lazy(() => import("@/pages/admin/LiveStreamingAdmin"));
const MeetingsDashboard = lazy(() => import("@/pages/admin/MeetingsDashboard"));
const AmazonBusinessSimple = lazy(() => import("@/pages/admin/AmazonBusinessSimple"));
const AdminEventTeamContacts = lazy(() => import("@/pages/admin-event-team-contacts"));
const SalaryManagement = lazy(() => import("@/pages/admin/SalaryManagement"));
const SalaryReports = lazy(() => import("@/pages/admin/SalaryReports"));

// Van admin pages
const AdminVanPricing = lazy(() => import("@/pages/admin/van-pricing"));
const AdminVanDrivers = lazy(() => import("@/pages/admin/van-drivers"));
const AdminVanRentals = lazy(() => import("@/pages/admin/van-rentals"));
const AdminVanWaves = lazy(() => import("@/pages/admin/van-waves"));
const AdminVanTransport = lazy(() => import("@/pages/admin/van-transport"));
const AdminVanFleet = lazy(() => import("@/pages/admin/van-fleet"));
const AdminTransportHub = lazy(() => import("@/pages/admin/transport-hub"));
const AdminVanDocumentsExpiring = lazy(() => import("@/pages/admin/van-documents-expiring"));
const AdminVanIncidents = lazy(() => import("@/pages/admin/van-incidents"));
const DriverTabletPage = lazy(() => import("@/pages/driver-tablet"));
const PanelWalkPage = lazy(() => import("@/pages/panel-walk"));
const ElectricalReviewPage = lazy(() => import("@/pages/electrical-review"));
const VanTvDisplay = lazy(() => import("@/pages/van-tv-display"));
const SignagePlayer = lazy(() => import("@/pages/signage-player"));
const SignageCounters = lazy(() => import("@/pages/signage-counters"));
const SignageCalibrate = lazy(() => import("@/pages/signage-calibrate"));
const SignageWallShare = lazy(() => import("@/pages/signage-wall-share"));
const SignageCaterer = lazy(() => import("@/pages/signage-caterer"));
const VanTvQrPage = lazy(() => import("@/pages/van-tv-qr"));
const VanScanPage = lazy(() => import("@/pages/van-scan"));
const VanCancelPage = lazy(() => import("@/pages/van-cancel"));
const VanDayPage = lazy(() => import("@/pages/van-day"));
const VanRentalPage = lazy(() => import("@/pages/van-rental"));
const VanStatusPage = lazy(() => import("@/pages/van-status"));

// LA Mirage Care portal admin pages - Lazy loaded
const CareAdminPage = lazy(() => import("@/pages/admin/care/CareAdminPage"));
const ScholarshipAdminPage = lazy(() => import("@/pages/admin/care/ScholarshipAdminPage"));
const ScholarshipApplicationDetail = lazy(() => import("@/pages/admin/care/ApplicationDetail"));
const MedicalApplicationDetail = lazy(() => import("@/pages/admin/care/MedicalApplicationDetail"));

// Accounting pages - Lazy loaded
const AccountingDashboard = lazy(() => import("@/pages/accounting/AccountingDashboard"));
const AuditorPortal = lazy(() => import("@/pages/auditor/AuditorPortal"));
// Fulfilment partner portal (Chunk 6 Task 9). Lazy, so its palette CSS and calendar never
// enter a client's or an admin's bundle.
const PartnerApp = lazy(() => import("@/pages/partner/PartnerApp"));
const GSTInvoiceReport = lazy(() => import("@/pages/accounting/GSTInvoiceReport"));
const ExpenseReport = lazy(() => import("@/pages/accounting/ExpenseReport"));

// Operations pages - Lazy loaded
const OperationsContacts = lazy(() => import("@/pages/admin/operations-contacts"));
const MyTasks = lazy(() => import("@/pages/admin/my-tasks"));
const Help = lazy(() => import("@/pages/admin/Help"));
const Expenses = lazy(() => import("@/pages/admin/Expenses"));

// Meeting pages - Lazy loaded
const LocalMeetings = lazy(() => import("@/pages/LocalMeetings"));
const MeetingRoom = lazy(() => import("@/pages/MeetingRoom"));
const JitsiMeetingRoom = lazy(() => import("@/pages/JitsiMeetingRoom"));
const MeetingGuestJoin = lazy(() => import("@/pages/MeetingGuestJoin"));
const MeetingLobby = lazy(() => import("@/components/meetings/MeetingLobby"));

// Visualizer - Event Designer (AR2026)
const Visualizer = lazy(() => import("@/pages/visualizer"));

// Client pages continued
import Feedback from "@/pages/feedback";
import NotificationsPage from "@/pages/notifications";
import Welcome from "@/pages/welcome";
import AITimelinePage from "@/pages/ai-timeline";
import ClientAddons from "@/pages/client-addons";
import LiveStreamPortal from "@/pages/LiveStreamPortal";
import CateringOrder from "@/pages/catering-order";
import CateringOrders from "@/pages/catering-orders";
import DanceProgramBooking from "@/pages/dance-program-booking";
import ArtistsPortfolio from "@/pages/ArtistsPortfolio";
import CommHubFixed from "@/pages/CommHubFixed";
import AmazonCallback from "@/pages/AmazonCallback";

// LA Mirage Live — public pages (no auth required)
import LiveLanding from "@/pages/live-public/LiveLanding";
import LiveNow from "@/pages/live-public/LiveNow";






// Core pages - Keep eager loaded
import Login from "@/pages/login";
import PasswordReset from "@/pages/password-reset";
import Home from "@/pages/home";
import Gallery from "@/pages/gallery";
import NotFound from "@/pages/not-found";
import LiveChat from "@/pages/live-chat";
import NoLinkedBooking from "@/components/onboarding/no-linked-booking";
import { needsOnboardingFor } from "@/v2/lib/noLinkedBooking";
import LoyaltyProgram from "@/pages/loyalty-program";
import UserProfile from "@/pages/user-profile";
import UserSettings from "@/pages/user-settings";
import { EmployeeApp } from "@/components/employee/EmployeeApp";

// Admin chat pages - Lazy loaded
const AdminLiveChatEnterprise = lazy(() => import("@/pages/admin-live-chat-enterprise"));
const SimpleChatDebug = lazy(() => import("@/pages/simple-chat-debug"));
const ChatDebugComplete = lazy(() => import("@/pages/chat-debug-complete"));
const AdminAnnouncements = lazy(() => import("@/pages/admin-announcements"));
const InternalMaintenance = lazy(() => import("@/pages/InternalMaintenance"));
const VenueManagement = lazy(() => import("@/pages/vms"));
const Communication = lazy(() => import("@/pages/Communication"));


const NewBookingShowcase = lazy(() => import('./pages/new-booking-showcase'));
// OnlineBookingPage + PaymentPage removed — dead scaffolding (no server endpoints)
const AIDashboardPage = lazy(() => import('./pages/ai-dashboard'));

// Raffle / Lucky Draw pages - Independent feature (no auth required)
import { RaffleLayout } from '@/components/raffle/RaffleLayout';
const RaffleLanding = lazy(() => import('./pages/raffle/RaffleLanding'));
const RaffleRegister = lazy(() => import('./pages/raffle/RaffleRegister'));
const RaffleCheck = lazy(() => import('./pages/raffle/RaffleCheck'));
const RaffleWinners = lazy(() => import('./pages/raffle/RaffleWinners'));
const RaffleSchedule = lazy(() => import('./pages/raffle/RaffleSchedule'));
const RaffleTVDisplay = lazy(() => import('./pages/raffle-tv/RaffleTVDisplay'));
const RaffleIPadRegister = lazy(() => import('./pages/raffle-ipad/RaffleIPadRegister'));
const RaffleAdminLogin = lazy(() => import('./pages/raffle-admin/RaffleAdminLogin'));
const RaffleAdminDashboard = lazy(() => import('./pages/raffle-admin/RaffleAdminDashboard'));

// Captive Portal / WiFi pages - Independent feature (no auth required)
const CaptivePortalLanding = lazy(() => import('./pages/captive-portal/CaptivePortalLanding'));
const CaptivePortalRegister = lazy(() => import('./pages/captive-portal/CaptivePortalRegister'));
const CaptivePortalPhoto = lazy(() => import('./pages/captive-portal/CaptivePortalPhoto'));
const CaptivePortalSpin = lazy(() => import('./pages/captive-portal/CaptivePortalSpin'));
const CaptivePortalSuccess = lazy(() => import('./pages/captive-portal/CaptivePortalSuccess'));
const PhotoWallPublic = lazy(() => import('./pages/photo-wall/PhotoWallPublic'));
const PhotoWallTV = lazy(() => import('./pages/photo-wall/PhotoWallTV'));

// Captive Portal Admin pages
const CaptiveAdminLogin = lazy(() => import('./pages/captive-admin/CaptiveAdminLogin'));
const CaptiveAdminDashboard = lazy(() => import('./pages/captive-admin/CaptiveAdminDashboard'));
const CaptiveAdminEvents = lazy(() => import('./pages/captive-admin/CaptiveAdminEvents'));
const CaptiveAdminGuests = lazy(() => import('./pages/captive-admin/CaptiveAdminGuests'));
const CaptiveAdminPhotos = lazy(() => import('./pages/captive-admin/CaptiveAdminPhotos'));
const CaptiveAdminPrizes = lazy(() => import('./pages/captive-admin/CaptiveAdminPrizes'));

// VR Experience pages - Guest registration and TV display (no auth required)
const VRRegistration = lazy(() => import('./pages/vr/VRRegistration'));
const VRTVDisplay = lazy(() => import('./pages/vr/VRTVDisplay'));
const VRManagement = lazy(() => import('./pages/admin/VRManagement'));

// Loading component for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function AuthenticatedApp() {
  const { user, logout } = useAuth();
  // Manager v2 on the web is the default for phone/tablet widths (< 1024px), owner 2026-10-01; the
  // server can switch it off (user.uiFlags.managerV2Web). Subscribed, so rotating a tablet across
  // 1024px re-decides. Called unconditionally (hook rules); only the manager branches read it.
  const narrowViewport = useSyncExternalStore(subscribeNarrowViewport, isNarrowViewport, () => false);
  const managerV2Opts = { serverOn: (user as { uiFlags?: { managerV2Web?: boolean } } | null)?.uiFlags?.managerV2Web, narrow: narrowViewport };
  // force-v2 (owner 2026-10-01): client-side roles always get portal v2; only the server kill switch
  // (uiFlags.clientPortal === 'classic', env CLIENT_PORTAL_FORCE_V2=off) restores the old rule.
  const clientPortalFlag = (user as { uiFlags?: { clientPortal?: string } } | null)?.uiFlags?.clientPortal;

  // 2026-05-03 fix: when accessed via a wedding website subdomain
  // (e.g. radhikaandnirmal.lamirage.co) or a custom domain, ALWAYS render
  // the public wedding site — even if the visitor is logged in as an admin
  // or client. Without this, an admin testing the URL hits AdminDashboard
  // and sees their portal instead of Nirmal's wedding site, leading to a
  // false "site is broken" report. Mirror of the unauthenticated logic
  // further down in App.tsx.
  //
  // RESERVED_SUBDOMAINS — defense in depth. These hostnames are routed to
  // separate services by nginx (n8n, plugnmeet, etc.) so they normally never
  // reach this React app. But if anyone ever points one at this app by
  // accident, we don't want the wedding-site renderer firing on it (would
  // 404 the underlying API and show "Website Not Found"). Add new reserved
  // names here BEFORE creating a wedding website with a clashing slug.
  const RESERVED_SUBDOMAINS = ['www', 'api', 'admin', 'n8n', 'meet', 'staging', 'app', 'mail', 'cdn', 'static', 'drive'];
  const hostname = window.location.hostname.toLowerCase();
  // lamirage.pics → the Live Photos showcase landing (must run BEFORE the custom-
  // wedding-domain check below, which would otherwise treat it as a wedding site).
  if (hostname === 'lamirage.pics' || hostname.endsWith('.lamirage.pics')) {
    return <Suspense fallback={<PageLoader />}><PicsLanding /></Suspense>;
  }
  // drive.lamirage.co → the public studio upload surface. Must short-circuit
  // BEFORE the wedding-domain check below, exactly like lamirage.pics, and must
  // exist in BOTH this branch and the unauthenticated one — staff are the first
  // people to open the URL, and without this a logged-in admin gets the
  // dashboard instead of the page they are trying to check.
  if (isDriveHost(hostname)) {
    return <Suspense fallback={<PageLoader />}><DrivePage /></Suspense>;
  }
  const subdomainCandidate = (hostname.endsWith('.lamirage.co') && hostname !== 'lamirage.co')
    ? hostname.slice(0, -'.lamirage.co'.length).split('.')[0]
    : null;
  const isWeddingSubdomain = !!subdomainCandidate && !RESERVED_SUBDOMAINS.includes(subdomainCandidate);
  // ⚠️ `isCustomWeddingDomain` is TRUE for la-mirage.in (it is not lamirage.co
  // and not localhost). It is safe only because AppContent short-circuits that
  // host into the booking funnel before it ever reaches AuthenticatedApp — see
  // `isLaMirageInHost` there. If this block is ever hoisted above that guard,
  // the venue's ranking domain renders the wedding-website renderer, i.e.
  // "Website Not Found", on every URL.
  const isCustomWeddingDomain = hostname !== 'lamirage.co'
    && !hostname.endsWith('.lamirage.co')
    && !hostname.includes('localhost')
    && !hostname.includes('127.0.0.1')
    && !hostname.includes('replit');
  if (isWeddingSubdomain || isCustomWeddingDomain) {
    // On the couple's own domain, /photos serves their guest photo gallery
    // (AI live gallery + Find Me), resolved from the host. Everything else
    // renders the wedding website.
    if (/^\/photos\/?$/.test(window.location.pathname)) {
      return (
        <Suspense fallback={<PageLoader />}>
          <PublicGallery />
        </Suspense>
      );
    }
    // A *.lamirage.co subdomain is always a wedding website. An arbitrary custom
    // domain could be a LIVE-stream domain OR a wedding-website domain → resolve.
    if (isCustomWeddingDomain && !isWeddingSubdomain) {
      return <CustomDomainRouter />;
    }
    return (
      <Suspense fallback={<PageLoader />}>
        <WeddingWebsitePublic />
      </Suspense>
    );
  }

  // Public Help Center — the SAME page for everyone (logged in or not, any role).
  // Must be handled BEFORE the role branching, else a logged-in user's role switch
  // (which has no /help route) falls through to NotFound. Mirrors the wedding
  // subdomain early-return pattern above (window.location, no extra hook).
  if (/^\/help(\/|$)/.test(window.location.pathname)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/help/:slug" component={HelpCenter} />
          <Route path="/help" component={HelpCenter} />
        </Switch>
      </Suspense>
    );
  }

  // CRITICAL: Always call hooks in same order to prevent React hooks violation
  // Render different interfaces based on role without early returns
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'event_manager' || user?.role === 'operations_manager';
  const isVendor = user?.role === 'vendor';
  const isEmployee = user?.role === 'employee';
  const isAuditor = user?.role === 'auditor';
  // A logged-in user ALWAYS has a role from the server. If it's momentarily absent
  // (post-login state-settle race), don't fall through to the CLIENT portal — wait.
  // Otherwise an auditor/admin flashes the client page for a frame before correcting.
  if (user && !user.role) {
    return <PageLoader />;
  }
  const isClient = !isAdmin && !isManager && !isVendor && !isEmployee && !isAuditor;

  // 🔴 The client portal is the FALLTHROUGH of this component: every branch below is an
  // `if (role is X) return <X portal/>`, and the final `return` is the CLIENT product with
  // no default branch. So any role this file does not recognise renders the client portal —
  // sidebar, dashboard, guest list, invoices, wedding website. `isClient` above is a
  // NEGATIVE list and re-opens the moment a role is added.
  //
  // This is a POSITIVE list. A role not on it gets the refusal screen below, never the
  // client product. When the partner portal ships (ENABLE_PARTNER_PORTAL), add a
  // `if (user?.role === 'partner') return <PartnerApp/>` branch ABOVE this line — do NOT
  // add 'partner' to this list.
  const CLIENT_PORTAL_ROLES = ['client', 'collaborator', 'planner'];

  // Fulfilment partner (caterer / photographer) — self-contained portal, its own nav.
  //
  // ⚠️ MUST STAY ABOVE the `useQuery(['/api/weddings'])` further down this component
  // (~line 793). Placing a role branch AFTER a hook changes the hook count between
  // renders and React throws "Rendered fewer hooks than expected" the moment a partner
  // signs out or the role settles. The auditor branch below is here for the same reason.
  //
  // PartnerApp renders neither <Header/> nor <Sidebar/>, so there is no partner path into
  // the client/admin chrome — and none into `getAdminPaletteConfig`, which hands an
  // unrecognised role the ADMIN command palette (palette-config.ts:319-333) but is only
  // reached from header.tsx:46.
  if (user?.role === 'partner') {
    return (
      <Suspense fallback={<PageLoader />}>
        <PartnerApp />
      </Suspense>
    );
  }

  // External auditor — self-contained portal, sees NOTHING else (must be before the
  // client fallthrough, which would otherwise render the client portal for this role).
  if (isAuditor) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={AuditorPortal} />
          <Route>
            <Redirect to="/" />
          </Route>
        </Switch>
      </Suspense>
    );
  }

  // Employee interface - FEATURE FLAG CONTROLLED
  if (isEmployee && import.meta.env.VITE_EMPLOYEE_ACCESS_ENABLED === 'true') {
    console.log('🚀 EMPLOYEE ROUTING: Detected employee user, routing to EmployeeApp');
    return <EmployeeApp user={user} />;
  }
  // Mobile web parity with the store app (audit CREW-3, 2026-10-01): an employee's web session gets
  // the same read-only crew schedule the app shows — it used to dead-end on "No portal for this
  // account" with no way out. The server still decides whether the login is linked to a staff
  // profile (/api/crew/me → 403 ⇒ CrewApp signs out). Above every hook below, like the auditor branch.
  if (isEmployee) {
    return <Suspense fallback={<PageLoader />}><CrewApp /></Suspense>;
  }

  // Admin interface
  if (isAdmin) {
    // The admin route table alone (no Header/Sidebar): the desktop layout below wraps it, and the
    // store app frames listed pages of it inside the v2 manager shell (AppClassicPage, audit 2026-10-01).
    const classicAdminRoutes = (
            <Suspense fallback={<PageLoader />}>
              <Switch>
                <Route path="/" component={AdminDashboard} />
              <Route path="/dashboard" component={IntelligenceDashboard} />
              <Route path="/admin/clients/:id" component={AdminClientDetails} />
              <Route path="/admin/clients" component={AdminClients} />
              <Route path="/admin/users" component={AdminUsers} />
              <Route path="/admin/weddings" component={AdminWeddings} />
              <Route path="/admin/vendors" component={AdminVendorsRestored} />
              <Route path="/admin/vendor-management" component={AdminVendorsRestored} />
              <Route path="/admin/mobile-navigation" component={AdminMobileNavigationMinimal} />
              <Route path="/valet-tv" component={lazy(() => import("./pages/valet-tv-display"))} />
              <Route path="/valet-alert" component={ValetDeskAlert} />
              <Route path="/valet/retrieval" component={lazy(() => import("./pages/valet-car-retrieval"))} />
              <Route path="/admin/reviews" component={AdminReviewManagement} />
              <Route path="/admin/loyalty" component={AdminLoyalty} />
              <Route path="/admin/reschedule-requests" component={AdminRescheduleRequests} />
              <Route path="/admin/signage/live" component={AdminSignageLive} />
              <Route path="/admin/signage" component={AdminSignageHub} />
              <Route path="/admin/signage/guest-photos" component={AdminSignageGuestPhotos} />
              <Route path="/admin/signage/live-walls" component={AdminSignageLiveWalls} />
              <Route path="/admin/employees" component={AdminEmployeeCalendar} />
              <Route path="/events-calendar" component={EventsCalendar} />
              <Route path="/admin/stage-designs" component={StageDesignManagement} />
              <Route path="/admin/stage-selections" component={AdminStageSelections} />
              <Route path="/admin/financial" component={FinancialDashboard} />
              <Route path="/admin/finance-hub" component={FinanceHub} />
              <Route path="/admin/events-hub" component={EventsHub} />
              <Route path="/admin/vision-studio" component={VisionStudioAdmin} />
              <Route path="/admin/packages" component={AdminPackages} />
              <Route path="/admin/analytics" component={AdminAnalytics} />
              <Route path="/admin/performance" component={AdminPerformance} />
              <Route path="/admin/bookings" component={AdminBookings} />
              <Route path="/admin/journey-studio" component={AdminJourneyStudio} />
              {/* Kept working: this was the ONLY way to reach the abandoned list before the
                  Bookings page existed, so a bookmark may point here. */}
              <Route path="/admin/abandoned-bookings" component={AdminAbandonedBookings} />
              <Route path="/admin/announcements" component={AdminAnnouncements} />
              <Route path="/admin/settings" component={AdminSettings} />
              <Route path="/admin/event-team-contacts" component={AdminEventTeamContacts} />
              <Route path="/admin/email-management" component={AdminEmailManagement} />
              <Route path="/admin/support" component={SupportWorkspace} />
              <Route path="/admin/files" component={AdminFiles} />
              <Route path="/admin/storage" component={AdminStorage} />
              <Route path="/admin/backups" component={AdminBackups} />
              <Route path="/admin/n8n" component={AdminN8n} />
              <Route path="/admin/payments" component={EventPayments} />
              <Route path="/admin/payout-providers" component={PayoutProvidersPage} />
              {/* Invoices live in Finance Hub now — this path used to render the
                  admin dashboard, which was simply wrong. */}
              <Route path="/admin/invoices">{() => <Redirect to="/admin/finance-hub?tab=invoices" />}</Route>
              <Route path="/admin/accounting" component={AccountingDashboard} />
              <Route path="/admin/gst-report" component={GSTInvoiceReport} />
              <Route path="/admin/expense-report" component={ExpenseReport} />
              <Route path="/admin/operations-contacts" component={OperationsContacts} />
              <Route path="/admin/my-tasks" component={MyTasks} />
              <Route path="/admin/help" component={Help} />
              <Route path="/admin/expenses" component={Expenses} />
              <Route path="/admin/whatsapp-templates" component={WhatsAppTemplatesAdmin} />
              <Route path="/admin/otp-testing" component={AdminOTPTesting} />
              <Route path="/admin/ai-knowledge" component={AdminAIKnowledge} />
              <Route path="/admin/welcome-config" component={AdminWelcomeConfig} />
              <Route path="/admin/venue-config" component={AdminVenueConfig} />
              <Route path="/admin/event-time-operations" component={AdminEventTimeOperations} />
              <Route path="/admin/post-event-operations" component={AdminPostEventOperations} />
              <Route path="/admin/timeline/:id" component={AdminTimelineDetails} />
              <Route path="/admin/mc-edit" component={AdminMcEdit} />
              <Route path="/admin/mc-management" component={AdminMCManagement} />
              <Route path="/admin/mc-schedules" component={AdminMCSchedules} />
              <Route path="/admin/mc-calendar" component={AdminMCCalendar} />
              <Route path="/admin/dance-calendar" component={AdminDanceCalendar} />
              <Route path="/admin/valet-management" component={AdminValetManagement} />
              <Route path="/admin/van-transport" component={AdminVanTransport} />
              <Route path="/admin/van/fleet" component={AdminVanFleet} />
              <Route path="/admin/van/documents-expiring" component={AdminVanDocumentsExpiring} />
              <Route path="/admin/van/incidents" component={AdminVanIncidents} />
              <Route path="/admin/van/pricing" component={AdminVanPricing} />
              <Route path="/admin/van/drivers" component={() => { window.location.href = '/admin/van-transport'; return null; }} />
              <Route path="/admin/van/rentals" component={() => { window.location.href = '/admin/van-transport'; return null; }} />
              <Route path="/admin/van/waves/:weddingId" component={AdminVanWaves} />
              {/* Transport hub — wraps van-transport / van-fleet / van-pricing / content in horizontal tabs */}
              <Route path="/admin/transport" component={AdminTransportHub} />
              <Route path="/admin/transport/calendar" component={AdminTransportHub} />
              <Route path="/admin/transport/weddings" component={AdminTransportHub} />
              <Route path="/admin/transport/fleet" component={AdminTransportHub} />
              <Route path="/admin/transport/pricing" component={AdminTransportHub} />
              <Route path="/admin/transport/content" component={AdminTransportHub} />
              <Route path="/client-transport" component={ClientTransport} />
              <Route path="/admin/domain-requests" component={AdminDomainRequests} />
              <Route path="/admin/story-weaver" component={StoryWeaverAdmin} />
              <Route path="/admin/inventory" component={InventoryDashboard} />
              <Route path="/admin/internal-maintenance" component={InternalMaintenance} />
              <Route path="/admin/venue-management" component={VenueManagement} />
              <Route path="/admin/vr-management" component={VRManagement} />
              <Route path="/admin/local-purchase" component={LocalPurchaseManagement} />
              <Route path="/admin/addons" component={AdminAddons} />
              <Route path="/admin/artists" component={ArtistsManagement} />
              <Route path="/admin/photographer-devices" component={PhotographerDevices} />
              <Route path="/admin/live-photos" component={AdminLivePhotos} />
              <Route path="/admin/live-photos/classic" component={LivePhotoDashboard} />
              <Route path="/admin/live-streaming" component={LiveStreamingAdmin} />
              <Route path="/admin/catering-advances" component={CateringAdvances} />
              <Route path="/admin/catering" component={AdminCatering} />
              <Route path="/admin/ai-assistant" component={AdminAIAssistantPage} />
              <Route path="/admin/ai-dashboard" component={AIAdminDashboard} />
              <Route path="/admin/agents" component={AgentsPage} />
              <Route path="/admin/live-chat" component={AdminLiveChatEnterprise} />
              <Route path="/admin/live-chat-enterprise" component={AdminLiveChatEnterprise} />
              {/* /admin/whatsapp is the manager-v2 WhatsApp address; with v2 off it was a 404 card (re-audit R10). */}
              <Route path="/admin/whatsapp">{() => <Redirect to="/admin/live-chat-enterprise" />}</Route>
              <Route path="/admin/whatsapp/:sessionId">{() => <Redirect to="/admin/live-chat-enterprise" />}</Route>
              <Route path="/debug-chat" component={SimpleChatDebug} />
              <Route path="/debug-complete" component={ChatDebugComplete} />
              <Route path="/admin/callerdesk">{() => <Redirect to="/admin/voice-call-history" />}</Route>
              <Route path="/admin/voice-agent" component={VoiceAgentAdmin} />
              <Route path="/admin/voice-call-history" component={VoiceCallHistory} />
              <Route path="/admin/web-call-test" component={WebCallTest} />
              <Route path="/admin/web-call-funnel" component={WebCallFunnel} />
              <Route path="/admin/security" component={SecurityPage} />
              <Route path="/admin/notifications" component={NotificationAdmin} />
              <Route path="/admin/amazon" component={AmazonBusinessSimple} />
              <Route path="/admin/amazon/callback" component={AmazonCallback} />
              <Route path="/communication" component={Communication} />
              <Route path="/comm-hub" component={CommHubFixed} />
              <Route path="/admin/salary" component={SalaryManagement} />
              <Route path="/admin/salary-reports" component={SalaryReports} />
              <Route path="/admin/salary-approval" component={SalaryAdminApproval} />
              <Route path="/admin/mobile-app" component={MobileAppManagement} />

              {/* Meeting Routes - Jitsi Integration */}
              <Route path="/meetings" component={MeetingsDashboard} />
              <Route path="/admin/meetings" component={MeetingsDashboard} />
              <Route path="/meeting/:id" component={JitsiMeetingRoom} />

              <Route path="/admin/dance-approvals" component={AdminDanceApprovals} />
              <Route path="/visualizer" component={Visualizer} />
              <Route path="/event-day-form" component={EventForm} />
              <Route path="/zoho-settings" component={ZohoSettings} />
              <Route path="/zoho-desk-settings" component={ZohoDeskSettings} />
              <Route path="/notifications" component={NotificationsPage} />
              <Route path="/profile" component={UserProfile} />
              <Route path="/settings" component={UserSettings} />

              {/* LA Mirage Care portal admin — detail routes MUST come before index */}
              <Route path="/admin/care/scholarship/applications/:id" component={ScholarshipApplicationDetail} />
              <Route path="/admin/care/medical/applications/:id" component={MedicalApplicationDetail} />
              <Route path="/admin/care" component={CareAdminPage} />
              {/* /admin/scholarship/* redirects to /admin/care for the 30-day deprecation window */}
              <Route path="/admin/scholarship/applications/:id">
                {(params: any) => {
                  window.history.replaceState(null, '', `/admin/care/scholarship/applications/${params.id}`);
                  return <ScholarshipApplicationDetail />;
                }}
              </Route>
              <Route path="/admin/scholarship">
                {() => {
                  window.history.replaceState(null, '', '/admin/care#scholarship');
                  return <CareAdminPage />;
                }}
              </Route>

              <Route component={NotFound} />
              </Switch>
            </Suspense>
    );
    const classicAdmin = (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 min-w-0 min-h-screen pb-16 md:pb-0">
            {classicAdminRoutes}
          </main>
        </div>
        <LiveCallPopup />
      </div>
    );
    // Store app (Task B6 + audit 2026-10-01 item 3): always the v2 manager app; a path without a v2
    // screen gets the classic PAGE framed in the v2 shell when listed (lib/app/appClassic.ts), else the
    // "Open on lamirage.co" card — never this desktop layout.
    if (isAppBuild()) {
      return <Suspense fallback={<PageLoader />}><ManagerV2App classic={<AppClassicPage routes={classicAdminRoutes} />} /><AppPushPrompt audience="manager" /></Suspense>;
    }
    // Manager v2: ON for phone/tablet widths unless the server switch is off; ?mgr=v2 / ?mgr=v1 override
    // per browser (flag.ts). classicAdmin is the fallthrough for every path v2 does not own, so every
    // admin-only page stays reachable. Desktop: exactly the classic.
    if (isManagerV2Enabled(user?.role, managerV2Opts)) {
      return <Suspense fallback={<PageLoader />}><ManagerV2App classic={classicAdmin} /></Suspense>;
    }
    return classicAdmin;
  }

  // Event Manager and Operations Manager users get management interface
  if (user?.role === 'event_manager' || user?.role === 'operations_manager') {
    // The manager route table alone (no Header/Sidebar): the desktop layout below wraps it, and the
    // store app frames listed pages of it inside the v2 manager shell (AppClassicPage, audit 2026-10-01).
    const classicManagerRoutes = (
            <Suspense fallback={<PageLoader />}>
              <Switch>
                <Route path="/" component={Dashboard} />
                <Route path="/dashboard" component={IntelligenceDashboard} />
                <Route path="/admin/clients/:id" component={AdminClientDetails} />
              <Route path="/admin/clients" component={AdminClients} />
              <Route path="/admin/stage-selections" component={AdminStageSelections} />
              <Route path="/events-calendar" component={EventsCalendar} />
              <Route path="/admin/valet-management" component={AdminValetManagement} />
              <Route path="/admin/domain-requests" component={AdminDomainRequests} />
              <Route path="/admin/story-weaver" component={StoryWeaverAdmin} />
              <Route path="/admin/employees" component={AdminEmployeeCalendar} />
              <Route path="/admin/payments" component={EventPayments} />
              <Route path="/admin/inventory" component={InventoryDashboard} />
              <Route path="/admin/internal-maintenance" component={InternalMaintenance} />
              <Route path="/admin/local-purchase" component={LocalPurchaseManagement} />
              <Route path="/admin/catering-advances" component={CateringAdvances} />
              <Route path="/admin/support" component={SupportWorkspace} />
              <Route path="/admin/files" component={AdminFiles} />
              <Route path="/admin/email-management" component={AdminEmailManagement} />
              <Route path="/admin/mc-edit" component={AdminMcEdit} />
              <Route path="/admin/mc-management" component={AdminMCManagement} />
              <Route path="/admin/mc-calendar" component={AdminMCCalendar} />
              {/* Bookings (owner 2026-09-12). ⚠️ SAME MISS AS THE ONE BELOW: the sidebar entry
                  and the API role were both added and this registration was not, so the link
                  404'd for every event manager. A menu item is not a route.
                  The page hides Holidays/Traffic/Settings for this role, and those APIs are
                  admin-only regardless. */}
              <Route path="/admin/bookings" component={AdminBookings} />
              <Route path="/admin/abandoned-bookings" component={AdminAbandonedBookings} />
              {/* Phase D: managers already had mc-calendar/mc-edit but not the
                  request-processing queue — the backend requireAdmin allows
                  event_manager, only this route registration was missing. */}
              <Route path="/admin/mc-schedules" component={AdminMCSchedules} />
              <Route path="/admin/dance-calendar" component={AdminDanceCalendar} />
              <Route path="/admin/inventory" component={InventoryDashboard} />
              <Route path="/admin/internal-maintenance" component={InternalMaintenance} />
              <Route path="/admin/venue-management" component={VenueManagement} />
              <Route path="/admin/vr-management" component={VRManagement} />
              <Route path="/admin/local-purchase" component={LocalPurchaseManagement} />
              <Route path="/admin/catering-advances" component={CateringAdvances} />
              <Route path="/admin/live-chat-enterprise" component={AdminLiveChatEnterprise} />
              {/* /admin/whatsapp is the manager-v2 WhatsApp address; with v2 off it was a 404 card (re-audit R10). */}
              <Route path="/admin/whatsapp">{() => <Redirect to="/admin/live-chat-enterprise" />}</Route>
              <Route path="/admin/whatsapp/:sessionId">{() => <Redirect to="/admin/live-chat-enterprise" />}</Route>
              <Route path="/admin/callerdesk">{() => <Redirect to="/admin/voice-call-history" />}</Route>
              <Route path="/admin/voice-agent" component={VoiceAgentAdmin} />
              <Route path="/admin/voice-call-history" component={VoiceCallHistory} />
              <Route path="/admin/web-call-test" component={WebCallTest} />
              <Route path="/admin/security" component={SecurityPage} />
              <Route path="/communication" component={Communication} />
              <Route path="/comm-hub" component={CommHubFixed} />
              <Route path="/admin/salary" component={SalaryManagement} />
              <Route path="/admin/salary-reports" component={SalaryReports} />
              <Route path="/admin/operations-contacts" component={OperationsContacts} />
              <Route path="/admin/my-tasks" component={MyTasks} />
              <Route path="/admin/help" component={Help} />
              <Route path="/admin/expenses" component={Expenses} />
              <Route path="/admin/financial" component={FinancialDashboard} />
              <Route path="/admin/finance-hub" component={FinanceHub} />
              <Route path="/admin/events-hub" component={EventsHub} />
              <Route path="/admin/vision-studio" component={VisionStudioAdmin} />
              <Route path="/admin/live-streaming" component={LiveStreamingAdmin} />
              {/* Live Photos + Signage — event/operations managers run these on-site;
                  backend auth already allows them (livePhotos STAFF_ROLES, signage isStaff),
                  only these route registrations were missing (sidebar links 404'd). */}
              <Route path="/admin/live-photos" component={AdminLivePhotos} />
              <Route path="/admin/live-photos/classic" component={LivePhotoDashboard} />
              <Route path="/admin/signage/live" component={AdminSignageLive} />
              <Route path="/admin/signage" component={AdminSignageHub} />
              <Route path="/admin/signage/guest-photos" component={AdminSignageGuestPhotos} />
              <Route path="/admin/signage/live-walls" component={AdminSignageLiveWalls} />

              {/* Care portal — detail routes MUST come before the index route.
                  Plain <Route> (no conditional) — wouter <Switch> only matches direct
                  Route children, so a `{cond && <Route/>}` was silently 404ing for event managers. */}
              <Route path="/admin/care/scholarship/applications/:id" component={ScholarshipApplicationDetail} />
              <Route path="/admin/care/medical/applications/:id" component={MedicalApplicationDetail} />
              <Route path="/admin/care" component={CareAdminPage} />

              {/* Van Transport — event_manager + operations_manager need the
                  same hub as admin (rentals, pricing, drivers, guest waves,
                  quick links) so they can set up a wedding's return-wave
                  system on event day. Backend auth already allows them via
                  requireAdminAccess(['admin', 'event_manager']). */}
              <Route path="/admin/van-transport" component={AdminVanTransport} />
              <Route path="/admin/van/fleet" component={AdminVanFleet} />
              <Route path="/admin/van/documents-expiring" component={AdminVanDocumentsExpiring} />
              <Route path="/admin/van/incidents" component={AdminVanIncidents} />
              <Route path="/admin/van/pricing" component={AdminVanPricing} />
              <Route path="/admin/van/waves/:weddingId" component={AdminVanWaves} />
              <Route path="/admin/van/drivers" component={() => { window.location.href = '/admin/van-transport'; return null; }} />
              <Route path="/admin/van/rentals" component={() => { window.location.href = '/admin/van-transport'; return null; }} />
              {/* Transport hub — wraps van-transport / van-fleet / van-pricing / content in horizontal tabs */}
              <Route path="/admin/transport" component={AdminTransportHub} />
              <Route path="/admin/transport/calendar" component={AdminTransportHub} />
              <Route path="/admin/transport/weddings" component={AdminTransportHub} />
              <Route path="/admin/transport/fleet" component={AdminTransportHub} />
              <Route path="/admin/transport/pricing" component={AdminTransportHub} />
              <Route path="/admin/transport/content" component={AdminTransportHub} />

              {/* Meeting Routes - Jitsi Integration */}
              <Route path="/meeting/:id" component={JitsiMeetingRoom} />
              <Route path="/meetings" component={MeetingsDashboard} />
              <Route path="/admin/meetings" component={MeetingsDashboard} />

              <Route path="/admin/mfa" component={MFAManagement} />
              <Route path="/event-day-form" component={EventForm} />
              <Route path="/notifications" component={NotificationsPage} />
              <Route path="/profile" component={UserProfile} />
              <Route path="/settings" component={UserSettings} />

              {/* LA Mirage Care portal admin — detail routes MUST come before index */}
              <Route path="/admin/care/scholarship/applications/:id" component={ScholarshipApplicationDetail} />
              <Route path="/admin/care/medical/applications/:id" component={MedicalApplicationDetail} />
              <Route path="/admin/care" component={CareAdminPage} />
              <Route path="/admin/scholarship/applications/:id">
                {(params: any) => {
                  window.history.replaceState(null, '', `/admin/care/scholarship/applications/${params.id}`);
                  return <ScholarshipApplicationDetail />;
                }}
              </Route>
              <Route path="/admin/scholarship">
                {() => {
                  window.history.replaceState(null, '', '/admin/care#scholarship');
                  return <CareAdminPage />;
                }}
              </Route>

              <Route component={NotFound} />
              </Switch>
            </Suspense>
    );
    const classicManager = (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 min-w-0 min-h-screen ">
            {classicManagerRoutes}
          </main>
        </div>
        <LiveCallPopup />
      </div>
    );
    // Store app (Task B6 + audit 2026-10-01 item 3): always the v2 manager app; a path without a v2
    // screen gets the classic PAGE framed in the v2 shell when listed (lib/app/appClassic.ts), else the
    // "Open on lamirage.co" card — never this desktop layout.
    if (isAppBuild()) {
      return <Suspense fallback={<PageLoader />}><ManagerV2App classic={<AppClassicPage routes={classicManagerRoutes} />} /><AppPushPrompt audience="manager" /></Suspense>;
    }
    // Manager v2: ON for phone/tablet widths unless the server switch is off; ?mgr=v2 / ?mgr=v1 override
    // per browser (flag.ts). classicManager is the fallthrough for every path v2 does not own (unported
    // pages keep opening the existing page). Desktop: exactly the classic.
    if (isManagerV2Enabled(user?.role, managerV2Opts)) {
      return <Suspense fallback={<PageLoader />}><ManagerV2App classic={classicManager} /></Suspense>;
    }
    return classicManager;
  }

  // Vendor users get vendor interface
  if (user?.role === 'vendor') {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 min-h-screen ">
            <Suspense fallback={<PageLoader />}>
              <Switch>
                <Route path="/" component={VendorDashboard} />
              <Route path="/dashboard" component={IntelligenceDashboard} />
              <Route path="/vendor/dashboard" component={VendorDashboard} />
              <Route path="/vendor/profile" component={VendorProfile} />
              <Route path="/vendor/products" component={VendorProductsStandalone} />
              <Route path="/vendor/messages" component={VendorMessages} />
              <Route path="/vendor/bookings" component={VendorBookings} />
              <Route path="/vendor/reviews" component={VendorReviews} />
              <Route path="/notifications" component={NotificationsPage} />
              <Route path="/profile" component={UserProfile} />
              <Route path="/settings" component={UserSettings} />
              <Route component={NotFound} />
              </Switch>
            </Suspense>
          </main>
        </div>
      </div>
    );
  }

  // Client users - check if they need onboarding
  const { data: weddings, isLoading: weddingsLoading, error: weddingsError } = useQuery<any[]>({
    queryKey: ['/api/weddings'],
    // Every client-side role (collaborators and planners read their weddings from the same route).
    enabled: isClientSideRole(user?.role) && !!user?.id,
    retry: false,
  });

  // Only show onboarding if user is client, data has loaded successfully, and no weddings exist.
  //
  // ⚠️ VAN-RENTAL CUSTOMERS ARE NOT WEDDING CLIENTS (2026-08-09).
  // The public transport OTP endpoint (server/routes/transport/transportAuthRoutes.ts)
  // auto-creates accounts with role='client' and isTransportCustomer=true from a
  // phone number alone. They own no wedding, so they were being shown the WEDDING
  // onboarding wizard — a form they have no reason to fill in, and which cannot be
  // submitted anyway (its POST omits the required weddingDate, so it 400s every
  // time: 15 such failures logged for one client on 08-06/07). The wizard has no
  // skip and no logout, so it is a dead end.
  //
  // Excluding them here is the narrow fix. The wizard itself remains broken for
  // genuinely new clients and should be replaced with a "we can't find your
  // booking — contact us" screen; weddings are created by the booking flow, not
  // by clients.
  // force-v2 Part 6: every client-side role (client, collaborator, planner) — a collaborator whose
  // invitation was revoked or a planner not yet assigned gets the role-fitting "no booking" screen,
  // not an empty portal. Van-rental OTP clients stay excluded (v2/lib/noLinkedBooking.ts).
  const needsOnboarding = needsOnboardingFor({
    role: user?.role, id: user?.id, isTransportCustomer: (user as any)?.isTransportCustomer,
    weddingsLoading, weddingsError: !!weddingsError, weddings,
  });

  // The full-screen loader is for the FIRST weddings load only. Once that load has settled
  // (data OR error) for this user, never swap the app back to the loader: a refetch of a
  // query with no data flips it to pending (isLoading) again, which unmounted the portal,
  // whose own /api/weddings query re-fetched on mount, failed, remounted the portal... —
  // a tight loop (~700 req/s) whenever /api/weddings fails, e.g. an offline app launch.
  const weddingsSettledFor = useRef<number | null>(null);
  if (user?.id && !weddingsLoading && (weddings !== undefined || weddingsError)) weddingsSettledFor.current = user.id;
  const firstWeddingsLoad = weddingsLoading && weddingsSettledFor.current !== user?.id;

  // Show loading state while user data or wedding data is loading
  if ((isClientSideRole(user?.role) && firstWeddingsLoad) || !user?.id) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-yellow-600" />
          <p className="text-white">Loading your wedding data...</p>
        </div>
      </div>
    );
  }

  // Default branch for the client-portal fallthrough (see CLIENT_PORTAL_ROLES above).
  // Placed after every hook in this component so hook order is unchanged.
  if (!CLIENT_PORTAL_ROLES.includes(user?.role || '')) {
    return (
      <div className="min-h-dvh bg-black flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-white mb-3">No portal for this account</h1>
          <p className="text-white/70 text-sm">
            Your account role (<span className="font-mono">{user?.role || 'unknown'}</span>) does not
            have a portal on this site. If you believe this is wrong, contact LA Mirage.
          </p>
          {/* Never a dead end (audit CREW-3): the only way out used to be clearing site data. */}
          <button
            type="button"
            onClick={() => { void logout(); }}
            className="mt-6 inline-flex items-center justify-center rounded-full border border-yellow-600/60 px-5 py-2 text-sm font-medium text-yellow-500"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (needsOnboarding) {
    // Clients may not create weddings (owner 2026-09-28) — tell them how to get linked
    // (collaborators: ask the couple; planners: get assigned).
    return <NoLinkedBooking />;
  }

  // Client users get wedding planning interface.
  // Client routes, shared by the classic layout below and the v2 shell (which renders redesigned
  // screens itself and falls through to this table for the rest). `notFound` is the last route:
  // the v2 NotFound inside v2, the classic developer 404 only in the classic (kill-switch) layout.
  const renderClientRoutes = (notFound: ReactNode) => (
            <Switch>
              <Route path="/" component={Dashboard} />
            {/* Portal audit 2026-09-24: unfinished / legacy / staff-only screens are redirected
                for CLIENT roles only (this Switch). The staff route tables above keep their own
                registrations of /dashboard, /communication and /admin/ai-dashboard. */}
            <Route path="/dashboard">{() => <Redirect to="/" />}</Route>
            <Route path="/welcome" component={Welcome} />

            <Route path="/guests" component={GuestManagement} />

            <Route path="/invitations" component={Invitations} />
            <Route path="/budget" component={BudgetCalculator} />
            <Route path="/vendors" component={VendorDirectory} />
            <Route path="/vendor-marketplace">{() => <Redirect to="/vendors" />}</Route>
            <Route path="/wedding-vendors" component={WeddingVendors} />
            <Route path="/client/addons" component={ClientAddons} />
            <Route path="/live-stream" component={LiveStreamPortal} />
            <Route path="/live-photos" component={ClientLivePhotos} />
            <Route path="/packages" component={Packages} />
            <Route path="/packages/:id" component={PackageDetails} />
            <Route path="/checklist" component={WeddingChecklist} />
            {/* Couples' tasks live on /checklist; these used to hit the developer 404. */}
            <Route path="/my-tasks">{() => <Redirect to="/checklist" />}</Route>
            <Route path="/tasks">{() => <Redirect to="/checklist" />}</Route>
            <Route path="/timeline" component={WeddingTimeline} />
            <Route path="/ai-timeline" component={AITimelinePage} />
            <Route path="/ai-assistant" component={AIAssistantPage} />
            <Route path="/admin/ai-dashboard">{() => <Redirect to="/" />}</Route>
            <Route path="/menu">{() => <Redirect to="/menu-builder" />}</Route>
            <Route path="/menu/:weddingId">{() => <Redirect to="/menu-builder" />}</Route>
            <Route path="/menu-builder" component={MenuBuilderPage} />
            <Route path="/family-hub" component={FamilyHub} />
            <Route path="/event-screens" component={EventScreens} />
            <Route path="/client-transport" component={ClientTransport} />

            <Route path="/client/catering/order" component={CateringOrder} />
            <Route path="/client/catering/orders" component={CateringOrders} />
            <Route path="/dance-program-booking" component={DanceProgramBooking} />
            <Route path="/website">{() => <Redirect to="/website-v2" />}</Route>
            <Route path="/wedding-website">{() => <Redirect to="/website-v2" />}</Route>
            <Route path="/weddings/:weddingId/website">{() => <Redirect to="/website-v2" />}</Route>
            <Route path="/website-v2" component={WeddingWebsiteWizard} />
            <Route path="/wedding-website-v2" component={WeddingWebsiteWizard} />
            <Route path="/wedding-website-wizard" component={WeddingWebsiteWizard} />
            <Route path="/weddings/:weddingId/website-v2" component={WeddingWebsiteWizard} />
            <Route path="/website-v2/manage" component={WeddingWebsiteManage} />
            <Route path="/wedding-website-v2/manage" component={WeddingWebsiteManage} />
            <Route path="/website-v2/manage/:id" component={WeddingWebsiteManage} />
            <Route path="/wedding-website/:id" component={WeddingWebsitePublic} />
            <Route path="/wedding-website-public/:id" component={WeddingWebsitePublic} />
            <Route path="/wedding-website-editor" component={WeddingWebsiteEditor} />
            <Route path="/story-weaver" component={StoryWeaverPage} />
            <Route path="/notes" component={WeddingNotes} />
            <Route path="/visualizer" component={Visualizer} />
            <Route path="/event-day-form" component={EventForm} />
            <Route path="/event-form" component={EventForm} />
            <Route path="/mc-scheduling" component={MCScheduling} />
            <Route path="/bookings" component={Bookings} />
            <Route path="/bookings/artists" component={ArtistsPortfolio} />
            <Route path="/invoices" component={Invoices} />
            <Route path="/support" component={SupportTickets} />
            <Route path="/files" component={Files} />
            <Route path="/feedback" component={Feedback} />
            <Route path="/comm-hub" component={CommHubFixed} />
            <Route path="/loyalty-program" component={LoyaltyProgram} />
            <Route path="/ai-assistant" component={AIAssistantPage} />
            <Route path="/communication">{() => <Redirect to="/support" />}</Route>
            <Route path="/live-chat">{() => <Redirect to="/support" />}</Route>

            {/* Meeting Routes for Clients - Jitsi Integration */}
            <Route path="/meeting/:id" component={JitsiMeetingRoom} />
            <Route path="/meetings" component={MeetingsDashboard} />
            {/* /book-online + /payment/:bookingId removed — dead scaffolding (their
                server endpoints /api/booking/create + /api/payment/* never existed).
                Page files kept on disk; rebuild on the canonical pay pattern if needed. */}
            <Route path="/ai-dashboard">{() => <Redirect to="/" />}</Route>
            <Route path="/stage-decoration/:id">
              {() => <EventForm />}
            </Route>
            <Route path="/notifications" component={NotificationsPage} />
            <Route path="/profile" component={UserProfile} />
            <Route path="/settings" component={UserSettings} />

            <Route>{() => notFound}</Route>
            </Switch>
  );
  const clientRoutes = renderClientRoutes(<NotFound />);

  // Portal v2 for EVERY client-side role (client, collaborator, planner) at every width — force-v2,
  // owner 2026-10-01. ?ui=v1, a stored classic choice and ?view=classic are ignored for them; see
  // client/src/v2/lib/flag.ts portalFor (matrix-tested). Not a hook: safe after the conditional returns above.
  if (isUiV2Enabled(user?.role, clientPortalFlag)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <V2ClientApp classicRoutes={renderClientRoutes(<V2NotFound />)} allowClassicView={honourViewClassic(user?.role, clientPortalFlag)} />
      </Suspense>
    );
  }

  // Classic client layout — reachable ONLY under the server kill switch (uiFlags.clientPortal === 'classic'),
  // which restores the pre-force rule (collaborators / planners, or a client who chose ?ui=v1).
  return (
    <div className="min-h-dvh bg-black">
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 min-w-0 min-h-dvh pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
          <Suspense fallback={<PageLoader />}>
            {clientRoutes}
          </Suspense>
        </main>
      </div>
      <MobileTabBar />
    </div>
  );
}

function AppContent() {
  const { user, isLoading, token } = useAuth();
  // Subscribed explicitly: the app's signed-out branch decides from the path (AppPublicShell tabs).
  const [appPath] = useLocation();
  usePageTracking(); // Track page views for analytics
  useStaleBuildReload(user?.role); // force-v2: an old open CLIENT-SIDE tab reloads onto the current bundle (lib/staleBuild.ts)
  useFirstPartyAnalytics(); // First-party analytics (spec 2026-09-30) — cookieless, GPC/DNT honoured

  console.log('🚀 AppContent render - user:', user, 'isLoading:', isLoading);

  // ---------------------------------------------------------------------------
  // la-mirage.in host-based routing — the public booking funnel
  //
  // Mirrors the lamirage.live block below: runs INSIDE the providers, and
  // short-circuits BEFORE every path bypass, before the `isLoading` screen,
  // before the `!user` public Switch and before `AuthenticatedApp`. That
  // ordering is the whole mechanism, and it buys three separate things:
  //
  //   1. On la-mirage.in ONLY the routes in this block exist. The client
  //      portal is unreachable — including `AuthenticatedApp`'s final
  //      unconditional `return` of the client portal, which has no default
  //      branch and therefore renders the FULL portal for any role it does not
  //      recognise. That return is now simply not on the code path for this
  //      host, for an anonymous visitor or a logged-in one.
  //   2. It beats the `isCustomWeddingDomain` predicate (three copies further
  //      down), which classifies any non-lamirage.co hostname as a couple's
  //      custom wedding domain and would render "Website Not Found" on every
  //      URL of the venue's ranking domain. lamirage.pics needed exactly this
  //      early return for exactly this reason.
  //   3. It runs for logged-in users too. An admin who opens la-mirage.in sees
  //      the funnel a client sees, not their own portal — the same lesson as
  //      the 2026-05-03 wedding-subdomain fix.
  //
  // Conversely, on lamirage.co the funnel routes are never registered at all,
  // so an unauthenticated booking funnel cannot appear inside the portal.
  // ---------------------------------------------------------------------------
  const marketingHost = typeof window !== 'undefined' ? window.location.hostname : '';
  // 🔴 MUST run BEFORE the isLaMirageInHost branch below. That predicate matches
  // the apex AND ANY SUBDOMAIN — deliberately — so it is TRUE for
  // drive.la-mirage.in, and if it wins the race the upload page silently renders
  // the booking funnel instead. Pinned by client/src/lib/__tests__/driveHost.test.ts.
  if (isDriveHost(marketingHost)) {
    return <Suspense fallback={<PageLoader />}><DrivePage /></Suspense>;
  }
  if (isLaMirageInHost(marketingHost)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={BookingFunnel} />
          <Route path="/booking" component={BookingFunnel} />
          <Route path="/pricing" component={PricingPage} />
          {/* 🔴 MUST be registered BEFORE the catch-all below. Every Razorpay payment link
              points its callback at this path, and the catch-all redirects to `/` — so
              without this line a client who has just paid ₹50,000 is bounced back to an
              empty booking form with no word about their money. */}
          {/* Children form, not `component={}` — the page takes an optional `embedded`
              prop and wouter types `component` as `ComponentType<RouteComponentProps>`. */}
          <Route path="/book/confirming"><BookingConfirming /></Route>
          {/* Nothing else exists on this host. Same shape as the auditor
              portal's catch-all: a role/host that "sees NOTHING else" sends
              every other path home rather than falling through to a 404
              wearing portal chrome. */}
          <Route>
            <Redirect to="/" />
          </Route>
        </Switch>
      </Suspense>
    );
  }

  // ---------------------------------------------------------------------------
  // LA Mirage Live host-based routing
  // Runs INSIDE providers (QueryClientProvider, AuthProvider, TooltipProvider).
  // Short-circuits before the main portal so lamirage.live/* renders the
  // public streaming UI without any wedding-portal chrome.
  // ---------------------------------------------------------------------------
  const liveHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isLiveHost = liveHost === 'lamirage.live' || liveHost.endsWith('.lamirage.live');
  if (isLiveHost) {
    const LIVE_APEX_LABELS = new Set(['www', 'stream']);
    const liveLabel = liveHost.replace(/\.lamirage\.live$/, '');
    const isApex = liveHost === 'lamirage.live' || LIVE_APEX_LABELS.has(liveLabel);
    if (isApex) {
      // Apex / www / stream → landing + live-now list + /w/:slug
      return (
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/live" component={LiveNow} />
            <Route path="/w/:slug" component={LiveWatch} />
            <Route path="/" component={LiveLanding} />
            <Route component={LiveLanding} />
          </Switch>
        </Suspense>
      );
    } else {
      // Couple subdomain (e.g. priya-arjun.lamirage.live) → that couple's watch page.
      // LiveWatch reads window.location.hostname to resolve the slug via
      // GET /api/live/by-subdomain/:subdomain when no route param is present.
      return (
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/w/:slug" component={LiveWatch} />
            <Route path="/" component={LiveWatch} />
            <Route component={LiveWatch} />
          </Switch>
        </Suspense>
      );
    }
  }

  // Direct route handling to bypass routing issues
  const currentPath = window.location.pathname;

  // CRITICAL: Photographer app must bypass auth for Android APK
  if (currentPath === "/photographer" || currentPath === "/photographer-app") {
    console.log('📸 Loading PhotographerApp directly (bypass auth check)');
    return <PhotographerApp />;
  }

  // Print station bypass
  if (currentPath === "/print-station") {
    return <PrintStation />;
  }

  // Plan E6: the public account-deletion page (Google Play's deletion URL). Standalone for every
  // visitor, signed in or not — it only explains the process and sends a request to the owner.
  if (currentPath === "/account-deletion") {
    return <Suspense fallback={<div className="min-h-screen bg-black" />}><AccountDeletion /></Suspense>;
  }

  // Old guest links (WhatsApp "photos ready" button, venue-TV QR) forward to
  // /gallery/<slug> — standalone so a logged-in couple/staff phone gets there too
  // (these routes only existed in the logged-out Switch before).
  if (currentPath.startsWith("/live-photos/gallery/") || currentPath.startsWith("/live-photos/register/")) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0d0d0f]" />}>
        {currentPath.startsWith("/live-photos/gallery/") ? <LegacyGalleryLink /> : <LegacyRegisterLink />}
      </Suspense>
    );
  }

  // Public event photo gallery (Honcho-model) — always render standalone,
  // even for logged-in users (guests may also be couples/admins).
  if (currentPath.startsWith("/gallery/")) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0d0d0f]" />}>
        <PublicGallery />
      </Suspense>
    );
  }

  // The couple's public wedding website (and the editor's "Preview" of it) is a GUEST page with its own
  // header. Inside the signed-in portal it rendered under the client shell: no back link, the dock
  // floating over the site (audit R4-15). Standalone for every visitor, like /gallery/ above.
  if (/^\/wedding-website-public\/[^/]+\/?$/.test(currentPath)) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
        <Switch><Route path="/wedding-website-public/:id" component={WeddingWebsitePublic} /></Switch>
      </Suspense>
    );
  }

  // In-platform contract signing — the link arrives on the SAME phone where the
  // client may be logged into the portal, so this must render regardless of
  // auth state (registering it only in the !user Switch would 404 for them).
  // ⚠️ Distinct from /contract/sign/:token (catering vendor flow).
  if (currentPath.startsWith("/sign/")) {
    const signToken = currentPath.slice("/sign/".length).split(/[/?#]/)[0];
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0d0d0f]" />}>
        <SignContract token={signToken} />
      </Suspense>
    );
  }

  // Guest RSVP link (/rsvp/<token>) — guest-facing, and the couple often opens a
  // guest's link on the phone where they are logged in, which fell through to
  // the portal's 404. Render it standalone regardless of auth state. (Token
  // links under /rsvp/t/… and the old /rsvp/accept|decline/… are served by the
  // server and never reach the SPA.)
  if (/^\/rsvp\/[^/]+\/?$/.test(currentPath)) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
        <GuestRSVP />
      </Suspense>
    );
  }

  // Collaborator invitation link (/invite/<token>) for a SIGNED-IN user (force-v2 Part 6): it
  // arrives on WhatsApp / email on the phone where the invitee may already be signed in (a couple
  // invited to a friend's wedding, a planner). Before role branching — web portal, staff layouts
  // and the store app — it would otherwise fall through to a 404 or the app's role home.
  // Signed out it stays in the !user Switch below (web) / the app's sign-in → returnTo.
  if (user && /^\/invite\/[^/]+\/?$/.test(currentPath)) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
        <AcceptInvite />
      </Suspense>
    );
  }

  // Public tax-invoice link — same reasoning as /sign/ above: it arrives on WhatsApp,
  // on the same phone where the client may be logged into the portal.
  if (currentPath.startsWith("/inv/")) {
    const invToken = currentPath.slice("/inv/".length).split(/[/?#]/)[0];
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0d0d0f]" />}>
        <ViewInvoice token={invToken} />
      </Suspense>
    );
  }

  // Driver tablet bypass — uses its own JWT, not the regular user session
  if (currentPath === "/driver") {
    return <DriverTabletPage />;
  }

  // Venue TV display bypass — uses kiosk token, not user session
  if (currentPath === "/urbaniatv") {
    return <VanTvDisplay />;
  }

  // Signage player bypass — on-TV kiosk, no user session, auth is the screenKey
  if (currentPath === "/signage/player") {
    return <SignagePlayer />;
  }

  // Catering counter tablet bypass — venue kiosk, no user session, auth is the
  // ?k= device key (pathname check only, so the query string survives).
  if (currentPath === "/signage/counters") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0a0f1a]" />}>
        <SignageCounters />
      </Suspense>
    );
  }

  // LED wall calibration (Signage Phase D) — opened on the box itself while setting the CMS130 crop; no session.
  if (currentPath === "/signage/calibrate") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
        <SignageCalibrate />
      </Suspense>
    );
  }

  // Guest share-to-screen (Signage Phase D) — public, no session; the per-event token is the only key.
  if (currentPath.startsWith("/wall/")) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
        <SignageWallShare />
      </Suspense>
    );
  }

  // Caterer screen setup (Signage catering) — public, no session; the per-event link token is the only key.
  if (currentPath.startsWith("/caterer-screens/")) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0a0f1a]" />}>
        <SignageCaterer />
      </Suspense>
    );
  }

  // Public van rental landing page — always render the marketing page
  // even when an admin/client is logged in (no portal chrome).
  if (currentPath === "/van-rental" || currentPath === "/urbania") {
    return (
      <Suspense fallback={<PageLoader />}>
        <VanRentalPage />
      </Suspense>
    );
  }

  // Venue TV QR-code display — public kiosk page for a 55" TV at the
  // venue entrance/reception. No auth, no token — the server looks up
  // today's wedding and returns the already-signed scan URL.
  if (currentPath === "/urbaniaqr") {
    return (
      <Suspense fallback={<PageLoader />}>
        <VanTvQrPage />
      </Suspense>
    );
  }

  // Van guest subscription public pages — no auth, token-based
  if (currentPath.startsWith("/v/s/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/v/s/:token" component={VanScanPage} />
        </Switch>
      </Suspense>
    );
  }
  if (currentPath.startsWith("/v/c/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/v/c/:token" component={VanCancelPage} />
        </Switch>
      </Suspense>
    );
  }
  if (currentPath.startsWith("/v/r/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/v/r/:ref" component={VanStatusPage} />
        </Switch>
      </Suspense>
    );
  }
  // Van driver's day page — public (signed token), pre-auth like /v/s, /v/c, /v/r above.
  if (currentPath.startsWith("/van/day/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/van/day/:token" component={VanDayPage} />
        </Switch>
      </Suspense>
    );
  }

  if (currentPath === "/vendor-products" || currentPath === "/vendor/products") {
    return <VendorProductsStandalone />;
  }

  if (isLoading) {
    console.log('⏳ Showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2L3 7v11a2 2 0 002 2h10a2 2 0 002-2V7l-7-5z" />
              <path d="M7 13h6v6H7v-6z" />
            </svg>
          </div>
          <p className="text-neutral-500">Loading...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Mobile app (client-app/, isAppBuild()). Signed out: the public wedding site under the app's own menu
  // (AppPublicShell: Home · Booking · Pricing · Your Day · Login); every other path goes to
  // /login (on the web "/" is the lamirage.co marketing page). Signed in:
  // lib/appRoleHome decides — couples get portal v2 (flag.ts forces it), managers get the
  // manager v2 app, everyone else a calm "coming soon — use lamirage.co" with Sign out.
  // ---------------------------------------------------------------------------
  if (isAppBuild()) {
    // Crew-scoped token (Task C5): CrewApp owns that session (it validates via /api/auth/crew/me and
    // hard-logs-out on 401/403). Checked BEFORE `user` — a crew token never loads a client profile.
    if (tokenScope(token) === "crew") {
      return <Suspense fallback={<PageLoader />}><CrewApp /></Suspense>;
    }
    if (!user) {
      // Signed out: the public wedding site under the app's own menu (owner 2026-10-03) — Home,
      // Booking, Features, Your Day — plus Login. Any other path needs a session: keep the deep
      // link and go to Login, so after sign-in the visitor lands back on it.
      if (!publicTabFor(appPath)) {
        rememberAppReturnTo(window.location.pathname + window.location.search);
        return <Redirect to="/login" />;
      }
      return (
        <Suspense fallback={<PageLoader />}>
          <AppPublicShell>
            <Switch>
              {/* Task C4: crew & staff (WhatsApp code, crew-scoped token) and managers (email + password + MFA). */}
              <Route path="/login/crew" component={CrewOtpSignIn} />
              <Route path="/login/staff" component={StaffSignIn} />
              <Route path="/login" component={Login} />
              <Route><Redirect to="/login" /></Route>
            </Switch>
          </AppPublicShell>
        </Suspense>
      );
    }
    // Signed in but still on /login (e.g. a slow venue network: the loader gave up, the gate
    // sent us here, then the profile load succeeded) — for EVERY role, before the role switch:
    // a manager would otherwise land on the "opens on lamirage.co" card for '/login'.
    if (window.location.pathname === "/login") return <Redirect to={consumeAppReturnTo()} />;
    if (window.location.pathname.startsWith("/login/")) return <Redirect to={consumeAppReturnTo()} />; // /login/staff (Task C4)
    // Role → home in the app (Task B6; crew in Task C5).
    // A web employee's full session (email + password) also gets CrewApp; the server decides whether
    // the account is linked (/api/crew/me → 403 crew_not_linked ⇒ hard logout).
    const home = appRoleHome(user.role, {
      crewScoped: false,
      crewLinked: user.role === "employee",
      clientPortal: (user as { uiFlags?: { clientPortal?: string } }).uiFlags?.clientPortal,
    });
    if (home === "crew") return <Suspense fallback={<PageLoader />}><CrewApp /></Suspense>;
    if (home === "manager") {
      // Manager v2 regardless of the web's switch, mounted by AuthenticatedApp's admin / manager branch
      // (its isAppBuild() return) — that is where each role's classic route table lives, so listed
      // classic pages render INSIDE the v2 shell (AppClassicPage) and the rest get the "Open on
      // lamirage.co" card. The app never renders the desktop classic admin (Header/Sidebar).
      return <AuthenticatedApp />;
    }
    if (home !== "portal") return <AppComingSoon />;
    // Couples: portal v2 + the push sheet (Task D6).
    return <><AuthenticatedApp /><Suspense fallback={null}><AppPushPrompt audience="couple" /></Suspense></>;
  }

  // Mobile web parity (audit CREW-3): a crew-scoped token (WhatsApp-code sign-in, /login/crew) opens the
  // crew schedule on the web too. Only on the crew's own paths — any public page (RSVP, live watch,
  // galleries …) still renders below for a crew member who opens one.
  if (tokenScope(token) === "crew" && isCrewWebPath(window.location.pathname)) {
    return <Suspense fallback={<PageLoader />}><CrewApp /></Suspense>;
  }

  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          {/* Crew & staff sign in with a WhatsApp code on the web too (audit CREW-3). */}
          <Route path="/login/crew" component={CrewOtpSignIn} />
          <Route path="/login" component={Login} />
          <Route path="/gallery" component={Gallery} />
          <Route path="/password-reset" component={PasswordReset} />
          <Route path="/reset-password" component={PasswordReset} />
          <Route path="/admin/*" component={() => {
            window.location.href = '/login';
            return <div>Redirecting to login...</div>;
          }} />
          <Route path="/client-transport" component={() => {
            window.location.href = '/login';
            return <div>Redirecting to login...</div>;
          }} />
          <Route path="/vendor/signup" component={VendorSignup} />
          <Route path="/vendor-signup" component={VendorSignup} />
          <Route path="/vendor/login" component={VendorLogin} />
          <Route path="/vendor-login" component={VendorLogin} />
          <Route path="/vendor/dashboard" component={VendorDashboard} />
          <Route path="/vendor-dashboard" component={VendorDashboard} />
          <Route path="/vendor/products" component={VendorProductsStandalone} />
          <Route path="/vendor-products" component={VendorProductsStandalone} />
          <Route path="/valet" component={ValetBooking} />
          <Route path="/valet-admin" component={ValetAdmin} />
          <Route path="/valet-tv" component={ValetTVDisplay} />
          <Route path="/valet-alert" component={ValetDeskAlert} />
          <Route path="/valet/request/:token" component={ValetRequest} />
          {/* LA Mirage Live — public guest watch page (no auth) */}
          <Route path="/w/:slug" component={LiveWatch} />
          <Route path="/rsvp/:token" component={GuestRSVP} />
          <Route path="/invite/:token" component={AcceptInvite} />
          {/* /contract/sign/:token is served as standalone HTML by server/index.ts (registered
              before the SPA fallback) and posts to /api/catering/contract/remote-sign/:token. */}
          <Route path="/wedding-website/:id" component={WeddingWebsitePublic} />
          <Route path="/wedding-website-public/:id" component={WeddingWebsitePublic} />
          <Route path="/guest-photos/upload/:token" component={GuestPhotoUpload} />
          <Route path="/live-photos/register/:token" component={LegacyRegisterLink} />
          <Route path="/live-photos/gallery/:token" component={LegacyGalleryLink} />
          {/* Legacy guest gallery links (event summary / share caption / concierge
              built /photos/gallery/<token> until the 2026-09-26 reminder audit #85).
              Keeps every link already sent on WhatsApp working. */}
          <Route path="/photos/gallery/:token">
            {(params: { token: string }) => <Redirect to={`/live-photos/gallery/${params.token}`} replace />}
          </Route>
          <Route path="/gallery/:slug" component={PublicGallery} />
          {/* "Your Night" booth kiosk — venue touchscreen, one slug per event */}
          <Route path="/booth/:slug" component={Booth} />
          <Route path="/live-photos/slideshow/:weddingId" component={LiveSlideshow} />
          <Route path="/live-photos/tv" component={LiveSlideshowTV} />
          <Route path="/pics" component={PicsLanding} />
          <Route path="/help/:slug" component={HelpCenter} />
          <Route path="/help" component={HelpCenter} />
          <Route path="/concierge/:weddingId" component={ConciergeMode} />
          <Route path="/photographer" component={PhotographerApp} />
          <Route path="/photographer-app" component={PhotographerApp} />
          <Route path="/print-station" component={PrintStation} />
          <Route path="/driver" component={DriverTabletPage} />
          {/* Panel walk — venue electrical survey on a phone or iPad. Public route, gated by
              its own shared password inside the page (see server/routes/panelWalk.ts for why
              that is acceptable: it reaches nothing but photos of distribution boards). */}
          <Route path="/panel-walk" component={PanelWalkPage} />
          {/* The engineer's own surface — shared password, questions only, no costs or jobs. */}
          <Route path="/electrical-review" component={ElectricalReviewPage} />
          {/* Same page, opened on its "Phase 1 — Monitoring installation" tab, behind the same password. */}
          <Route path="/electrical-review/phase1" component={ElectricalReviewPage} />
          <Route path="/urbaniatv" component={VanTvDisplay} />
          <Route path="/urbaniaqr" component={VanTvQrPage} />
          <Route path="/v/s/:token" component={VanScanPage} />
          <Route path="/v/c/:token" component={VanCancelPage} />
          <Route path="/v/r/:ref" component={VanStatusPage} />
          <Route path="/van-rental" component={VanRentalPage} />
          <Route path="/urbania" component={VanRentalPage} />
          <Route path="/live-chat" component={LiveChat} />
          <Route path="/salary" component={SalaryAttendance} />

          {/* Meeting Routes for Public Access - PlugNMeet Integration */}
          <Route path="/meeting/guest/:roomName" component={MeetingGuestJoin} />
          <Route path="/meeting/:id" component={JitsiMeetingRoom} />

          {/* 🔴 Where every public-booking payment link returns its client.
              `publicBookingCallbackUrl()` builds `${PUBLIC_BOOKING_BASE_URL || BASE_URL}/book/confirming`,
              and BASE_URL on production is `https://lamirage.co` — so this host, not just the
              la-mirage.in branch above, has to answer it. No auth: the client is anonymous and
              has just paid. */}
          {/* Children form, not `component={}` — the page takes an optional `embedded`
              prop and wouter types `component` as `ComponentType<RouteComponentProps>`. */}
          <Route path="/book/confirming"><BookingConfirming /></Route>

          {/* /book-online + /payment/:bookingId removed — dead scaffolding (endpoints never existed) */}
          <Route path="/newbooking" component={NewBookingShowcase} />
          <Route path="/ai-dashboard" component={AIDashboardPage} />

          {/* Raffle / Lucky Draw Routes - Independent feature (no auth required) */}
          <Route path="/raffle" component={() => <RaffleLayout><RaffleLanding /></RaffleLayout>} />
          <Route path="/raffle/register" component={() => <RaffleLayout><RaffleRegister /></RaffleLayout>} />
          <Route path="/raffle/entry" component={() => <RaffleLayout><RaffleRegister /></RaffleLayout>} />
          <Route path="/raffle-entry" component={() => <RaffleLayout><RaffleRegister /></RaffleLayout>} />
          <Route path="/raffle/check" component={() => <RaffleLayout><RaffleCheck /></RaffleLayout>} />
          <Route path="/raffle/winners" component={() => <RaffleLayout><RaffleWinners /></RaffleLayout>} />
          <Route path="/raffle/schedule" component={() => <RaffleLayout><RaffleSchedule /></RaffleLayout>} />
          <Route path="/raffle-tv" component={() => <RaffleLayout><RaffleTVDisplay /></RaffleLayout>} />
          <Route path="/raffle-ipad" component={() => <RaffleLayout><RaffleIPadRegister /></RaffleLayout>} />
          <Route path="/raffle-admin" component={() => <RaffleLayout><RaffleAdminLogin /></RaffleLayout>} />
          <Route path="/raffle-admin/dashboard" component={() => <RaffleLayout><RaffleAdminDashboard /></RaffleLayout>} />

          {/* Captive Portal / WiFi Routes - Independent feature (no auth required) */}
          <Route path="/wifi" component={CaptivePortalLanding} />
          <Route path="/wifi/register" component={CaptivePortalRegister} />
          <Route path="/wifi/photo" component={CaptivePortalPhoto} />
          <Route path="/wifi/spin" component={CaptivePortalSpin} />
          <Route path="/wifi/success" component={CaptivePortalSuccess} />

          {/* Photo Wall Routes - Public display for guest photos */}
          <Route path="/photos" component={() => {
            // On a couple's wedding-website domain (subdomain or custom domain),
            // /photos is their guest photo gallery. On lamirage.co it's the WiFi
            // captive-portal photo wall (unchanged).
            const RES = ['www', 'api', 'admin', 'n8n', 'meet', 'staging', 'app', 'mail', 'cdn', 'static', 'drive'];
            const h = window.location.hostname.toLowerCase();
            const sub = (h.endsWith('.lamirage.co') && h !== 'lamirage.co') ? h.slice(0, -'.lamirage.co'.length).split('.')[0] : null;
            const isCouple = (!!sub && !RES.includes(sub))
              || (h !== 'lamirage.co' && !h.endsWith('.lamirage.co') && !h.includes('localhost') && !h.includes('127.0.0.1') && !h.includes('replit'));
            return isCouple ? <PublicGallery /> : <PhotoWallPublic />;
          }} />
          <Route path="/photo-wall" component={PhotoWallPublic} />
          <Route path="/photos-tv" component={PhotoWallTV} />
          <Route path="/photo-wall-tv" component={PhotoWallTV} />

          {/* Captive Portal Admin Routes - Password protected */}
          <Route path="/wifi-admin" component={CaptiveAdminLogin} />
          <Route path="/wifi-admin/dashboard" component={CaptiveAdminDashboard} />
          <Route path="/wifi-admin/events" component={CaptiveAdminEvents} />
          <Route path="/wifi-admin/guests" component={CaptiveAdminGuests} />
          <Route path="/wifi-admin/photos" component={CaptiveAdminPhotos} />
          <Route path="/wifi-admin/prizes" component={CaptiveAdminPrizes} />

          {/* VR Experience Routes - Guest registration (no auth required) */}
          <Route path="/vr/:eventCode" component={VRRegistration} />
          <Route path="/vr-display/:eventId" component={VRTVDisplay} />

          <Route path="/" component={() => {
            // 2026-05-03: keep this in sync with the AuthenticatedApp branch
            // RESERVED_SUBDOMAINS list at the top of this file. Defense in
            // depth — n8n/meet/etc. should never reach this React app (nginx
            // routes them to other services), but if they ever do, don't fire
            // the wedding-site renderer on those slugs.
            const RESERVED_SUBDOMAINS = ['www', 'api', 'admin', 'n8n', 'meet', 'staging', 'app', 'mail', 'cdn', 'static', 'drive'];
            const hostname = window.location.hostname.toLowerCase();
            const subdomainCandidate = (hostname.endsWith('.lamirage.co') && hostname !== 'lamirage.co')
              ? hostname.slice(0, -'.lamirage.co'.length).split('.')[0]
              : null;
            const isSubdomain = !!subdomainCandidate && !RESERVED_SUBDOMAINS.includes(subdomainCandidate);
            // ⚠️ Same caveat as the AuthenticatedApp copy: this is TRUE for
            // la-mirage.in, and only harmless because AppContent's
            // isLaMirageInHost guard returns before this Switch is reached.
            const isCustomDomain = hostname !== 'lamirage.co'
              && !hostname.endsWith('.lamirage.co')
              && !hostname.includes('localhost')
              && !hostname.includes('127.0.0.1')
              && !hostname.includes('replit');

            // lamirage.pics → Live Photos showcase (must beat the wedding-domain renderer).
            if (hostname === 'lamirage.pics' || hostname.endsWith('.lamirage.pics')) {
              return <PicsLanding />;
            }
            // drive.lamirage.co → public studio upload surface. Mirror of the
            // AuthenticatedApp branch above; keep the two in sync.
            if (isDriveHost(hostname)) {
              return <DrivePage />;
            }
            if (isSubdomain || isCustomDomain) {
              // /photos on the couple's domain → their guest photo gallery
              if (/^\/photos\/?$/.test(window.location.pathname)) {
                return <PublicGallery />;
              }
              return <WeddingWebsitePublic />;
            }
            return <Home />;
          }} />
          {/* L5 (audit 2026-09-27): a signed-out WhatsApp/email link into the client portal
              on lamirage.co goes to /login and comes back after sign-in (role=client only). */}
          <Route>{() => {
            if (shouldRememberSignedOutPath(window.location.hostname, window.location.pathname)) {
              rememberAppReturnTo(window.location.pathname + window.location.search);
              return <Redirect to="/login" />;
            }
            return <Home />;
          }}</Route>
        </Switch>
      </Suspense>
    );
  }

  return <AuthenticatedApp />;
}

function App() {
  console.log('🚀 App component rendering');
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            {/* In-page confirm/prompt for classic pages (no browser dialogs — owner rule 2026-10-01). */}
            <InPageDialogHost />
            {/* Every push navigation opens the next screen at the top — classic layouts had no reset (EM-5). */}
            <RouteScrollReset />
            <AppContent />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
